import { useEffect, useRef } from "react";

export const useResultsHistory = (sessionId, messages = [], results) => {
  const messageResults = useRef({});

  useEffect(() => {
    // Find the last user message to attach results to
    const lastUserMessageIndex = [...messages]
      .map((msg, i) => ({ msg, i }))
      .reverse()
      .find(({ msg }) => msg.role === "user" && msg.content)?.i;

    if (!sessionId || lastUserMessageIndex === undefined || !results) {
      return;
    }

    // Ensure results is always an array
    const resultsArray = Array.isArray(results)
      ? results
      : results
        ? [results]
        : [];

    // Attach results to the message object directly
    messages[lastUserMessageIndex].results = resultsArray;

    // Store in our ref too (for persistence)
    const history = messageResults.current || {};
    history[sessionId] = history[sessionId] || {};

    // Store per message index
    if (!history[sessionId][lastUserMessageIndex]) {
      history[sessionId][lastUserMessageIndex] = [];
    }
    history[sessionId][lastUserMessageIndex] = resultsArray;

    // Save to localStorage
    localStorage.setItem("messageResults", JSON.stringify(history));

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionId, results]);

  return {
    getLast: () => {
      const localHistory = localStorage.getItem("messageResults");
      const history = localHistory ? JSON.parse(localHistory) : {};
      history[sessionId] = history[sessionId] || {};

      // Get the latest saved results for this session
      const messageIndices = Object.keys(history[sessionId]).map(Number);
      if (messageIndices.length === 0) return [];

      const latestMessageIndex = Math.max(...messageIndices);
      const lastResults = history[sessionId][latestMessageIndex] || [];

      // Ensure we return an array
      return Array.isArray(lastResults)
        ? lastResults
        : lastResults
          ? [lastResults]
          : [];
    },
    getIndex: (index) => {
      // First try to get from the message itself
      if (messages[index] && messages[index].results) {
        const msgResults = messages[index].results;
        return Array.isArray(msgResults)
          ? msgResults
          : msgResults
            ? [msgResults]
            : [];
      }

      // Fall back to stored history
      const localHistory = localStorage.getItem("messageResults");
      const history = localHistory ? JSON.parse(localHistory) : {};

      if (history[sessionId] && history[sessionId][index]) {
        const storedResults = history[sessionId][index];
        return Array.isArray(storedResults)
          ? storedResults
          : storedResults
            ? [storedResults]
            : [];
      }

      return [];
    },
  };
};

export default useResultsHistory;
